import * as React from 'react';
import {styled} from '../../../utils';
import {position, borderWidth} from 'polished';
import {StepPosition, StepRendererProps, StepStatus} from '../types';

interface RootProps {
  vertical: boolean;
  center: boolean;
}

interface ConnectorProps {
  vertical: boolean;
  center: boolean;
}

interface LineProps {
  vertical: boolean;
  center: boolean;
}

interface LineFillProps {
  vertical: boolean;
  filled: boolean;
}

interface HeadProps {
  status: StepStatus;
  vertical: boolean;
  dot: boolean;
}

interface MainProps {
  center: boolean;
  vertical: boolean;
}

const Root = styled('div')<RootProps>(
  {
    position: 'relative',
    flexShrink: 1
  },
  ({vertical, theme}) => {
    if (vertical) {
      return {
        display: 'flex',
        minHeight: theme.steps.vertical.stepHeightCollapsed
      };
    }
    return {
      // for horizontal mode
      display: 'inline-block',
      whiteSpace: 'nowrap'
    };
  }
);

const Connector = styled('div')<ConnectorProps>(
  {
    position: 'relative',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0
  },
  ({vertical, center}) => {
    if (vertical) {
      return {
        width: 'auto',
        flexGrow: 0,
        flexDirection: 'column'
      };
    }
    return {
      // for horizontal mode
      height: 'auto',
      justifyContent: center ? 'center' : 'flex-start'
    };
  }
);

const Line = styled('div')<LineProps>(
  ({theme}) => ({
    position: 'absolute',
    background: theme.steps.lineColor.empty
  }),
  ({vertical, center, theme}) => {
    if (vertical) {
      return {
        width: theme.steps.lineWidth * 1,
        left: '50%',
        top: 0,
        bottom: 0
      };
    }
    return {
      // for horizontal mode
      height: theme.steps.lineWidth * 1,
      top: '50%',
      left: center ? '50%' : 0,
      right: center ? '-50%' : 0
    };
  }
);

const LineFill = styled('i')<LineFillProps>(
  ({
    theme: {
      steps: {lineWidth, lineStyle, lineColor}
    },
    vertical,
    filled
  }) => ({
    display: 'block',
    borderWidth: lineWidth / 2,
    borderStyle: lineStyle,
    borderColor: lineColor.filled,
    height: vertical && filled ? '100%' : 0,
    width: !vertical && filled ? '100%' : 0,
    transition: 'all .15s ease-in',
    boxSizing: 'border-box'
  })
);

const Head = styled('div')<HeadProps>(({theme: {steps: theme}, vertical, dot, status}) => {
  const style = dot ? 'dot' : 'circle';
  const filled = dot || (theme.circle.filled || (theme.circle.filledFinished && status === 'finished'));
  const {spaceAround, borderWidth: spaceAroundOffset} = theme.head;

  return {
    position: 'relative',
    zIndex: 1,
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'all .15s ease-in',
    boxSizing: 'border-box',
    borderRadius: '50%',
    borderStyle: 'solid',
    borderWidth: theme.head.borderWidth,
    borderColor: theme.head[status].color,
    background: filled ? theme.head[status].color : '#fff',
    color: !filled ? theme.head[status].textColor : '#fff',
    width: theme[style].size,
    height: theme[style].size,
    fontFamily: theme.head.fontFamily,
    fontSize: theme.head.fontSize,
    fontWeight: theme.head.fontWeight,
    // space around
    '::after': {
      content: `''`,
      borderRadius: '50%',
      borderStyle: 'solid',
      borderColor: '#fff',
      ...(vertical
        ? position('absolute', -(spaceAroundOffset + spaceAround), -spaceAroundOffset)
        : position('absolute', -spaceAroundOffset, -(spaceAroundOffset + spaceAround))),
      ...(vertical ? borderWidth(spaceAround, 0) : borderWidth(0, spaceAround))
    }
  };
});

const HeadInner = styled('div')({
  display: 'inline-block',
  userSelect: 'none',
  textAlign: 'center',
  lineHeight: 1
});

const Main = styled('div')<MainProps>(({center, vertical}) => ({
  whiteSpace: 'normal',
  textAlign: center && !vertical ? 'center' : 'left'
}));

const Title = styled('div')(({theme}) => ({
  fontFamily: theme.fontFamily.base,
  fontSize: theme.fontSize.large,
  fontWeight: 700
}));

const Content = styled('div')(({theme}) => ({
  fontFamily: theme.fontFamily.base,
  fontSize: theme.fontSize.base
}));

export default class StepRenderer extends React.Component<StepRendererProps, {}> {
  public render() {
    const {
      children,
      title: _title,
      stepPosition,
      stepNumber,
      stepIndex,
      isCurrentStep,
      renderTitle,
      status,
      center,
      vertical,
      customClassName: cls,
      customStyle: style,
      ...restProps
    } = this.props;

    const title =
      typeof renderTitle === 'function' ? (
        renderTitle({title: _title, status, stepPosition, stepNumber, stepIndex, isCurrentStep, center, vertical})
      ) : (
        <Title className={cls && cls.title} style={style && style.title}>
          {_title}
        </Title>
      );

    return (
      <Root vertical={vertical} center={center} {...restProps}>
        <Connector
          vertical={vertical}
          center={center}
          className={cls && cls.connector}
          style={style && style.connector}
        >
          {stepPosition !== StepPosition.LAST ? (
            <Line vertical={vertical} center={center}>
              <LineFill vertical={vertical} filled={status === 'finished'} />
            </Line>
          ) : null}
          {this._renderHead()}
        </Connector>
        <Main center={center} vertical={vertical} className={cls && cls.main} style={style && style.main}>
          {title}
          <Content className={cls && cls.content} style={style && style.content}>
            {children}
          </Content>
        </Main>
      </Root>
    );
  }

  private _renderHead() {
    const {stepHeads, status, stepPosition, stepNumber, stepIndex, isCurrentStep, dot, vertical} = this.props;

    if (stepHeads && typeof stepHeads[status] === 'function') {
      return stepHeads[status]!({stepPosition, stepNumber, stepIndex, isCurrentStep});
    }

    return (
      <Head status={status} dot={dot} vertical={vertical}>
        <HeadInner>{dot ? null : stepNumber}</HeadInner>
      </Head>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/Steps/private/StepRenderer.tsx